import { useContext, useEffect, useState } from 'react'
import { AuthContextApi } from '../../Context/api'
import {
    CircleStackIcon,
    PencilIcon,
    ClockIcon,
    ArrowPathIcon,
    CalendarIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline'
import Header from '../../Components/Header'
import ModalConfiguracaoBusca from '../../Components/Modal/ConfiguracaoBusca'

const formatDateTime = (value) => {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const intervaloLabel = (config) => {
    const tipo = config.tipo_intervalo_tempo === 'hours' ? 'hora(s)' : config.tipo_intervalo_tempo === 'days' ? 'dia(s)' : config.tipo_intervalo_tempo
    return `A cada ${config.intervalo_tempo} ${tipo}`
}

const ConfiguracaoBusca = () => {
    const { getConfiguracaoBusca, patchConfiguracaoBusca } = useContext(AuthContextApi)

    const [configs, setConfigs] = useState([])
    const [loading, setLoading] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [selected, setSelected] = useState(null)

    const load = async () => {
        setLoading(true)
        const data = await getConfiguracaoBusca({ page: 0, per_page: 100, sort: 'area', sort_dir: 'asc' })
        setConfigs(Array.isArray(data) ? data : [])
        setLoading(false)
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const openEdit = (config) => {
        setSelected(config)
        setModalOpen(true)
    }

    const handleSave = async (data) => {
        if (!selected) return
        const ok = await patchConfiguracaoBusca(selected.id, data)
        if (ok) {
            setModalOpen(false)
            setSelected(null)
            await load()
        }
    }

    return (
        <>
            <Header
                title="Busca de dados"
                description="Gerencie os intervalos de sincronização de dados do sistema"
                icon={CircleStackIcon}
                hasAction={false}
                hasReturn={false}
                color="yellow"
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-yellow mx-auto"></div>
                            <p className="mt-4 text-gray-600">Carregando configurações...</p>
                        </div>
                    </div>
                ) : configs.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="p-6 rounded-3xl bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 inline-block">
                            <CircleStackIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma configuração encontrada</h3>
                            <p className="text-gray-600">Não há configurações de busca cadastradas no sistema.</p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {configs.map((config) => (
                            <div
                                key={config.id}
                                className="relative bg-white rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden group"
                            >
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-brand-yellow/10 to-brand-yellow/5 rounded-full -translate-y-12 translate-x-12"></div>

                                <div className="relative p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center min-w-0">
                                            <div className="p-2 rounded-xl bg-gray-900 shadow-lg mr-3 flex-shrink-0">
                                                <CircleStackIcon className="w-5 h-5 text-brand-yellow" />
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="text-sm font-bold text-gray-900 truncate" title={config.area}>
                                                    {config.area}
                                                </h3>
                                                <p className="text-xs text-gray-500 truncate">{config.slug}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => openEdit(config)}
                                            className="p-2 bg-brand-yellow/20 text-gray-800 rounded-full hover:bg-brand-yellow/40 transition-colors duration-200 flex-shrink-0"
                                            title="Editar configuração"
                                        >
                                            <PencilIcon className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="mb-4 inline-flex items-center gap-2 bg-gray-900 text-brand-yellow rounded-full px-3 py-1.5 text-xs font-semibold">
                                        <ArrowPathIcon className="w-4 h-4" />
                                        {intervaloLabel(config)}
                                    </div>

                                    <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100 space-y-2.5">
                                        <div className="flex items-center">
                                            <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 mr-2">
                                                <CheckCircleIcon className="w-3.5 h-3.5" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-gray-700">Última execução</p>
                                                <p className="text-xs text-gray-600">{formatDateTime(config.ultima_execucao)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="p-1.5 rounded-lg bg-blue-100 text-blue-700 mr-2">
                                                <ClockIcon className="w-3.5 h-3.5" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-gray-700">Próxima execução</p>
                                                <p className="text-xs text-gray-600">{formatDateTime(config.proxima_execucao)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="p-1.5 rounded-lg bg-gray-200 text-gray-600 mr-2">
                                                <CalendarIcon className="w-3.5 h-3.5" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-gray-700">Criado em</p>
                                                <p className="text-xs text-gray-600">{formatDateTime(config.data_criacao)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ModalConfiguracaoBusca
                open={modalOpen}
                onClose={() => { setModalOpen(false); setSelected(null) }}
                config={selected}
                onSave={handleSave}
                color="yellow"
            />
        </>
    )
}

export default ConfiguracaoBusca
